import { useMutation, useQuery } from "@tanstack/react-query";
import { Card, Col, Form, Input, message, Row, Select } from "antd";
import { businessAreaService } from "../../../services/business-area.service";
import { useCallback, useEffect, useState } from "react";
import { userService } from "../../../services/user.service";
import { useNavigate, useParams } from "react-router";
import CardFooterAction from "../../../components/CardFooterAction";
import { useAuth } from "../../../hooks/useAuth";
import RenderIf from "../../../components/RenderIf";

const permissions = [
  "Banner",
  "Monitoring SR",
  "RCC",
  "SR Masuk",
  "Survey Pelanggan",
  "Analisa Berkas",
  "Data Terpasang",
  "Layanan Lab",
  "Informasi Gangguan",
  "Customer Care",
  "Master Data",
  "Pengaduan",
  "Report Sambungan Baru",
  "Stand Meter",
  "Air Tangki"
];

const marketingSubdivision = [
  { label: "Surveyor", value: "Surveyor" },
  { label: "Officer", value: "Officer" },
  { label: "Marketing", value: "Marketing" },
];

const rccSubdivision = [
  { label: "RCC Officer", value: "RCC Officer" },
  { label: "Distribution Officer", value: "Distribution Officer" },
  { label: "Modeling Officer", value: "Modelling Officer" },
  { label: "Operation Study", value: "Operation Study" },
  { label: "PEPM Officer", value: "PEPM Officer" },
];

export default function UserForm() {
  const [form] = Form.useForm()

  const navigate = useNavigate()

  const { id } = useParams()

  const { user } = useAuth()

  const division = Form.useWatch('division', form);
  const subdivision = Form.useWatch('subdivision', form);
  const abId = Form.useWatch('abId', form);

  const [marketingList, setMarketingList] = useState([])

  const { data: businessAreaData, isPending } = useQuery({
    queryKey: ['businessAreaData'],
    queryFn: () => businessAreaService.fetchAll(),
    staleTime: Infinity
  })

  const { data: userData } = useQuery({
    queryKey: ['userData', id],
    queryFn: () => userService.getById(id),
    enabled: !!id
  })

  useEffect(() => {
    if (!id || !userData?.data) return;

    // --- NORMALISASI ABID: tidak lagi pakai fungsi luar ---
    const rawAbId = userData?.data?.abId ?? userData?.data?.ab ?? [];

    const normalizedAbId = Array.isArray(rawAbId)
      ? rawAbId
        .map(item => {
          if (item == null) return null;

          if (typeof item === "object") {
            const value = item.OBJECTID ?? item.id ?? item.value ?? null;
            return Number(value);
          }

          return Number(item);
        })
        .filter(v => Number.isFinite(v))
      : [];

    // --- SET FIELDS VALUE ---
    const {
      id: userId,
      name,
      email,
      phone,
      username,
      employeeId,
      permissions: userPermissions,
      division: userDivision,
      subdivision: userSubdivision,
      marketingId
    } = userData.data;

    form.setFieldsValue({
      id: userId,
      name,
      email,
      username,
      employeeId,
      permissions: userPermissions ?? [],
      division: userDivision,
      subdivision: userSubdivision,
      phone,
      marketingId,
      abId: normalizedAbId
    });
  }, [id, userData, form]);

  useEffect(() => {
    if (!id || !userData?.data || !businessAreaData?.responseData) return;

    const rawAbId = userData?.data?.abId ?? userData?.data?.ab ?? [];
    const normalizedAbId = Array.isArray(rawAbId)
      ? rawAbId
        .map(item => {
          if (item == null) return null;
          if (typeof item === "object") {
            const value = item.OBJECTID ?? item.id ?? item.value ?? null;
            return Number(value);
          }
          return Number(item);
        })
        .filter(v => Number.isFinite(v))
      : [];

    // --- MARKETING MUTATION ---
    const userSubdivision = userData.data.subdivision;
    if (
      userSubdivision === "Surveyor" &&
      normalizedAbId.length > 0
    ) {
      const abList = businessAreaData.responseData
        .filter(e => normalizedAbId.includes(Number(e.OBJECTID)))
        .map(e => e.NAMA_AB);

      if (abList.length > 0) {
        marketingMutation.mutate(abList);
      }
    }
  }, [id, userData, businessAreaData]);

  // reset abId ketika subdivision berubah ke non-valid roles
  useEffect(() => {
    const validRoles = ['Officer', 'Surveyor', 'P&R Officer', 'Marketing'];
    if (division !== 'RCC' && subdivision && !validRoles.includes(subdivision)) {
      form.resetFields(['abId']);
    }
  }, [subdivision, division, form]);

  // jika user memilih Surveyor dan abId valid -> ambil marketing list
  useEffect(() => {
    if (subdivision === 'Surveyor' && Array.isArray(abId) && abId.length > 0 && businessAreaData?.responseData) {
      const abList = businessAreaData.responseData
        .filter(e => abId.includes(e.OBJECTID))
        .map(e => e.NAMA_AB)
      if (abList.length > 0) marketingMutation.mutate(abList)
    } else {
      // jika tidak memenuhi syarat, kosongkan marketingList
      setMarketingList([])
      form.setFieldsValue({ marketingId: undefined })
    }
  }, [subdivision, abId, businessAreaData, form])

  const marketingMutation = useMutation({
    mutationFn: val => userService.getMarketingByAB(val),
    onSuccess: (res) => {
      setMarketingList(res.data || [])
    },
    onError: (err) => {
      console.error(err)
      message.error('Gagal mengambil data marketing')
    }
  })

  const storeMutation = useMutation({
    mutationKey: ['userServiceStore'],
    mutationFn: val => userService.store(val),
    onSuccess: () => {
      message.success("Data berhasil ditambah")
      navigate('/master-data/user')
    },
    onError: err => {
      message.error("Data terjadi kesalahan")
      console.error(err)
    }
  })

  const updateMutation = useMutation({
    mutationKey: ['userServiceUpdate'],
    mutationFn: val => userService.update(val),
    onSuccess: () => {
      message.success("Data berhasil diubah")
      navigate('/master-data/user')
    },
    onError: err => {
      message.error("Data terjadi kesalahan")
      console.error(err)
    }
  })

  const handleSubmit = (val) => {

    const data = { ...val }

    if (subdivision === 'Officer' || subdivision === 'Marketing' || subdivision === 'Surveyor' || subdivision === 'P&R Officer') {
      const ab = businessAreaData?.responseData
        ?.filter(e => Array.isArray(val.abId) && val.abId.includes(e.OBJECTID))
        .map(e => ({
          NAMA_AB: e.NAMA_AB,
          AREA: e.AREA,
          OBJECTID: e.OBJECTID
        })) ?? []
      data['ab'] = ab
    } else {
      data['ab'] = []
    }

    // console.log for debugging if needed
    console.log('submit payload', data)

    if (id) updateMutation.mutate(data)
    else storeMutation.mutate(data)
  }

  return (
    <>
      <Card
        title="Tambah Data"
        actions={[
          <CardFooterAction
            key={"footer"}
            route={"/master-data/user"}
            onClick={() => form.submit()}
            loading={storeMutation.isPending || updateMutation.isPending}
            disabled={storeMutation.isPending || updateMutation.isPending}
          />,
        ]}
      >
        <Form layout="vertical" form={form} onFinish={handleSubmit}>
          <Form.Item name={"id"} hidden>
            <Input />
          </Form.Item>
          <Row gutter={[20]}>
            <Col span={12}>
              <Form.Item
                name={"employeeId"}
                label="ID Pegawai"
                rules={[
                  {
                    required: true,
                  },
                ]}
              >
                <Input />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name={"name"}
                label="Nama Lengkap"
                rules={[
                  {
                    required: true,
                  },
                ]}
              >
                <Input />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name={"email"}
                label="Email"
                rules={[
                  {
                    required: true,
                  },
                ]}
              >
                <Input />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name={"phone"}
                label="No Telepon"
                rules={[
                  {
                    required: true,
                  },
                ]}
              >
                <Input />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name={"division"}
                label="Divisi"
                rules={[
                  {
                    required: true,
                  },
                ]}
                initialValue={user?.user?.subdivision == "SPV" && "Marketing"}
              >
                <Select
                  onChange={() => {
                    form.resetFields(['subdivision', 'abId']);
                  }}
                  disabled={user?.user?.subdivision == "SPV"}
                  options={[
                    {
                      label: "Information Technology",
                      value: "Information Technology",
                    },
                    { label: "Laboratorium", value: "Laboratorium" },
                    { label: "DMCC", value: "DMCC" },
                    { label: "Marketing", value: "Marketing" },
                    { label: "⁠Customer Care", value: "⁠Customer Care" },
                    { label: "RCC", value: "RCC" },
                    { label: "P&R", value: "P&R" },
                  ]}
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name={"subdivision"}
                label="Subdivisi"
                rules={[
                  {
                    required: ["Marketing", "RCC"].includes(division),
                  },
                ]}
              >
                <Select
                  disabled={!["Marketing", "RCC", "P&R"].includes(division)}
                  options={
                    division === "Marketing"
                      ? [
                        ...(user?.user?.subdivision !== "SPV"
                          ? [{ label: "SPV", value: "SPV" }]
                          : []),
                        ...marketingSubdivision,
                      ]
                      : division === "RCC"
                        ? rccSubdivision
                        : division === "P&R"
                          ? [{ label: "P&R Officer", value: "P&R Officer" }]
                          : []
                  }
                />
              </Form.Item>
            </Col>
            <Col span={24}>
              <Form.Item
                name={"abId"}
                label="Area Bisnis"
                // initialValue={null}
                rules={[
                  {
                    required:
                      ['Officer', 'Suryeyor', 'P&R Officer'].includes(subdivision)
                      || division == 'RCC'
                  },
                ]}
              >
                <Select
                  disabled={
                    isPending ||
                    (
                      ![
                        "Officer",
                        "Surveyor",
                        "Marketing",
                        "P&R Officer",
                      ].includes(subdivision)
                      && division != 'RCC'
                    )
                  }
                  loading={isPending}
                  options={
                    businessAreaData?.responseData?.map((e) => ({
                      label: e.NAMA_AB,
                      value: e.OBJECTID,
                    })) || []
                  }
                  mode="multiple"
                  optionFilterProp="label"
                />
              </Form.Item>
            </Col>
            <RenderIf when={subdivision == "Surveyor"}>
              <Col span={24}>
                <Form.Item name={"marketingId"} label="Marketing">
                  <Select
                    options={marketingList.map((e) => ({
                      label: e.name,
                      value: e.id,
                    }))}
                    disabled={isPending}
                    loading={isPending}
                  />
                </Form.Item>
              </Col>
            </RenderIf>
            <Col span={24}>
              <Form.Item
                name={"permissions"}
                label="Hak Akses"
                rules={[
                  {
                    required: true,
                  },
                ]}
              >
                <Select
                  options={permissions.map((e) => ({ value: e, label: e }))}
                  mode="multiple"
                  filterOption
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name={"username"}
                label="Username"
                rules={[
                  {
                    required: true,
                  },
                ]}
              >
                <Input />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name={"password"}
                label={"Password " + (id ? "Baru" : "")}
                rules={[
                  {
                    required: !id,
                  },
                ]}
              >
                <Input />
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Card>
    </>
  );
}
